/**
 * Legacy Content Wrapper
 * 
 * Reusable wrapper around the legacy address page content section.
 * Extracted from the original legacy layout to allow reuse in v2 as experiment control.
 * 
 * This preserves the exact legacy layout structure:
 * - Two-column layout (left: content, right: absolute-positioned sidebar)
 * - Sidebar with position: absolute, top: -7em (overlapping hero)
 * - Sidebar child with position: sticky, top: 100px
 * 
 * Used by:
 * - Legacy address page (when no experiments are running)
 * - V2 address page as control for STICKY_HEADER_TABS experiment
 * 
 * Note: Similar listings sections should be rendered OUTSIDE this wrapper
 * to maintain the legacy behavior where the sidebar stops being sticky.
 */

import dynamic from 'next/dynamic';
import Cookies from 'js-cookie';
import { buildClassName } from 'utils/build-class-name';
import { MLML_AGENT_COOKIE_NAME } from 'constants/cookies';
import { getTenantById } from '@tenants';
import { useMyListingAgents } from 'hooks/use-my-listing-agents';
import LoadingText from 'components/loading-text';

import type { AddressPageCommonData, AddressPageEventHandlers } from '../shared';
import { SoldListingExperimentCard } from '../components/sold-listing-experiment-card';
import styles from './style.module.scss';

//#region Dynamic Imports
const ConnectionRequest = dynamic(() => import('components/connection-request'), {
  loading: () => <LoadingText />,
});

const FavouritePrompt = dynamic(() => import('../components/favourite-prompt'), {
  loading: () => <LoadingText />, ssr: false,
});

const MyListingAgentCard = dynamic(() => import('components/my-listing-agent-card'), {
  loading: () => <LoadingText />,
});

const MyLinkMyLeadCreate = dynamic(() => import('components/my-link-my-lead-create'), {
  loading: () => <LoadingText />,
});
//#endregion

export interface LegacyContentWrapperProps {
  data: AddressPageCommonData;
  handlers: AddressPageEventHandlers;
  /** Content to render in the left section (main content area) */
  mainContent: React.ReactNode;
  /** Additional top offset for sidebar sticky positioning (e.g., when sticky nav is shown) */
  sidebarStickyTopOffset?: number;
}

/**
 * LegacyContentWrapper
 * 
 * Wraps content in the legacy two-column layout structure with sticky sidebar.
 * The sidebar uses absolute positioning with negative top offset to overlap the hero/gallery.
 * 
 * Layout structure:
 * - Parent: position: relative
 * - Left section: Main content (width: calc(100% - 20em))
 * - Right section: Sidebar (position: absolute, top: -7em, right: 0)
 *   - Inner div: position: sticky, top: 100px (adjustable via sidebarStickyTopOffset)
 */
export function LegacyContentWrapper({
  data,
  handlers,
  mainContent,
  sidebarStickyTopOffset = 0,
}: LegacyContentWrapperProps) {
  const {
    tenantId,
    listing,
    isSaved,
    soldListingExperimentId,
  } = data;

  const {
    onSeeAllSavedListingsClick,
    onSaveListingClick,
  } = handlers;

  const tenant = getTenantById(tenantId);
  const showsMLMLCreate = tenant.features.MyLinkMyLead && !!Cookies.get(MLML_AGENT_COOKIE_NAME);
  const showAgentCard = tenant.features.MyLinkMyLead && !showsMLMLCreate;
  const { agents, shouldShow: hasAgents } = useMyListingAgents(listing);

  // Calculate sidebar sticky top position: base (100px) + additional offset
  const sidebarStickyTop = 100 + sidebarStickyTopOffset;

  return (
    <section className={styles.component}>
      <div className={styles.wrapper}>
        <div className={styles['listing-content']}>
          {/* Left Section: Main Content */}
          <div className={styles['left-section']}>
            {mainContent}
          </div>

          {/* Right Section: Sticky Sidebar (Desktop Only) */}
          <div className={buildClassName(
            styles['request-info-section'],
            showsMLMLCreate && styles['request-info-section-lowered']
          )}>
            <div style={{ top: `${sidebarStickyTop}px` }}>
              {showsMLMLCreate ? (
                <MyLinkMyLeadCreate listing={listing} />
              ) : (
                <>
                  {listing.isSold ? (
                    soldListingExperimentId ? (
                      <SoldListingExperimentCard mode={soldListingExperimentId} listing={listing} />
                    ) : null
                  ) : (
                    <>
                      <ConnectionRequest
                        listing={listing}
                        isAddressPage={true}
                        agentCardSlot={showAgentCard && hasAgents ? <MyListingAgentCard agents={agents} layout="row" /> : undefined}
                      />
                      <FavouritePrompt
                        listingId={listing.id}
                        isFavourited={isSaved}
                        onSeeAllSavedListingsClick={onSeeAllSavedListingsClick}
                        onAddToFavouritesClick={onSaveListingClick}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
